import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import {
  listBalances,
  listPayments,
  getCardPaymentConfig,
  chargeCardPayment,
  formatExpiryInput,
  parseExpiry,
  type CardPaymentConfig,
  type CardChargeResult,
  type OpaqueData,
} from "../api/billing";

type AcceptResponse = {
  messages: { resultCode: "Ok" | "Error"; message: { code: string; text: string }[] };
  opaqueData?: OpaqueData;
};

declare global {
  interface Window {
    Accept?: { dispatchData: (data: unknown, cb: (r: AcceptResponse) => void) => void };
  }
}

const PRESET_AMOUNTS = [50, 100, 250, 500, 1000];

function fmtMoney(n: number): string {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function newIdemKey(): string {
  const raw =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID().replace(/-/g, "")
      : Date.now().toString(36) + Math.random().toString(36).slice(2);
  return raw.slice(0, 20);
}

function formatCardDigits(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 19);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
}

type CardStatus =
  | "loading"
  | "ready"
  | "submitting"
  | "charging"
  | "charged"
  | "declined"
  | "pending"
  | "error";

function CreditCardTopUpPanel({
  config,
  onCredited,
}: {
  config: CardPaymentConfig;
  onCredited: () => void;
}) {
  const presets = PRESET_AMOUNTS.filter(
    (p) => p >= config.min_amount && p <= config.max_amount,
  );
  const initialAmount = presets.includes(100)
    ? "100"
    : String(presets[0] ?? Math.max(config.min_amount, 50));

  const [status, setStatus] = useState<CardStatus>(() =>
    typeof window !== "undefined" && window.Accept ? "ready" : "loading",
  );
  const [amountInput, setAmountInput] = useState(initialAmount);
  const [fullName, setFullName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [exp, setExp] = useState("");
  const [cardCode, setCardCode] = useState("");
  const [zip, setZip] = useState("");
  const [idemKey, setIdemKey] = useState(newIdemKey);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<CardChargeResult | null>(null);
  const inFlight = useRef(false);

  const acceptSrc = config.sandbox
    ? "https://jstest.authorize.net/v1/Accept.js"
    : "https://js.authorize.net/v1/Accept.js";

  useEffect(() => {
    if (window.Accept) {
      setStatus("ready");
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${acceptSrc}"]`,
    );
    if (existing) {
      const onLoad = () => setStatus("ready");
      const onErr = () => {
        setStatus("error");
        setErrorMsg("Unable to load Authorize.Net secure card library. Please refresh.");
      };
      existing.addEventListener("load", onLoad);
      existing.addEventListener("error", onErr);
      return () => {
        existing.removeEventListener("load", onLoad);
        existing.removeEventListener("error", onErr);
      };
    }

    const s = document.createElement("script");
    s.src = acceptSrc;
    s.async = true;
    s.dataset.acceptjs = "1";
    s.onload = () => setStatus("ready");
    s.onerror = () => {
      setStatus("error");
      setErrorMsg("Unable to load Authorize.Net secure card library. Please refresh.");
    };
    document.head.appendChild(s);
  }, [acceptSrc]);

  const parsedAmount = Number(amountInput);
  const amountValid =
    Number.isFinite(parsedAmount) &&
    parsedAmount >= config.min_amount &&
    parsedAmount <= config.max_amount &&
    Math.round(parsedAmount * 100) === Number((parsedAmount * 100).toFixed(4));

  function resetForAnotherPayment() {
    inFlight.current = false;
    setReceipt(null);
    setErrorMsg(null);
    setCardNumber("");
    setExp("");
    setCardCode("");
    setIdemKey(newIdemKey());
    setStatus(window.Accept ? "ready" : "loading");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (inFlight.current) return;
    setErrorMsg(null);

    if (!amountValid) {
      setStatus("error");
      setErrorMsg(
        `Please enter an amount between ${fmtMoney(config.min_amount)} and ${fmtMoney(config.max_amount)}.`,
      );
      return;
    }
    if (!window.Accept) {
      setStatus("error");
      setErrorMsg("Secure payment library is still loading. Please wait a moment and try again.");
      return;
    }
    const digits = cardNumber.replace(/\D/g, "");
    if (digits.length < 13 || digits.length > 19) {
      setStatus("error");
      setErrorMsg("Please enter a valid card number.");
      return;
    }
    const parsedExp = parseExpiry(exp);
    if (!parsedExp) {
      setStatus("error");
      setErrorMsg("Please enter the card expiration date as MM/YY.");
      return;
    }
    const cvv = cardCode.trim();
    if (!/^\d{3,4}$/.test(cvv)) {
      setStatus("error");
      setErrorMsg("Please enter the 3- or 4-digit security code (CVC).");
      return;
    }
    const cleanZip = zip.trim();
    if (cleanZip.length < 3) {
      setStatus("error");
      setErrorMsg("Please enter the billing ZIP / postal code.");
      return;
    }

    inFlight.current = true;
    setStatus("submitting");

    const secureData = {
      authData: {
        clientKey: config.public_client_key,
        apiLoginID: config.api_login_id,
      },
      cardData: {
        cardNumber: digits,
        month: parsedExp.month,
        year: parsedExp.year,
        cardCode: cvv,
        zip: cleanZip,
        fullName: fullName.trim(),
      },
    };

    window.Accept.dispatchData(secureData, (resp) => {
      if (resp.messages.resultCode === "Error" || !resp.opaqueData) {
        inFlight.current = false;
        setStatus("error");
        setErrorMsg(
          resp.messages.message?.[0]?.text ||
            "Could not verify those card details. Please check and try again.",
        );
        return;
      }

      setStatus("charging");
      chargeCardPayment({
        opaqueData: resp.opaqueData,
        amount: Number(parsedAmount.toFixed(2)),
        idempotency_key: idemKey,
        cardholder_name: fullName.trim(),
        billing_zip: cleanZip,
      })
        .then((res) => {
          if (res.ok) {
            setCardNumber("");
            setExp("");
            setCardCode("");
            setReceipt(res);
            setStatus("charged");
            onCredited();
            return;
          }
          if (res.declined) {
            // Clean decline: rotate idempotency key so the customer can fix
            // their card or try a different card.
            inFlight.current = false;
            setIdemKey(newIdemKey());
            setStatus("declined");
            setErrorMsg(
              res.message || "The card was declined. Please check the details or try another card.",
            );
            return;
          }
          // Held for review / pending: stay locked so we never double-charge.
          setStatus("pending");
          setErrorMsg(
            res.message ||
              "Your payment is being confirmed. Please do not resubmit — refresh your balance shortly.",
          );
          onCredited();
        })
        .catch((err: unknown) => {
          const ax = err as AxiosError<{ detail?: string; error?: string }>;
          const httpStatus = ax?.response?.status;
          const serverMsg =
            ax?.response?.data?.detail ||
            ax?.response?.data?.error ||
            (err instanceof Error ? err.message : null);

          if (
            httpStatus === 400 ||
            httpStatus === 403 ||
            httpStatus === 422 ||
            httpStatus === 429 ||
            httpStatus === 503
          ) {
            // Rejected before contacting Authorize.Net -> safe to unlock.
            inFlight.current = false;
            setStatus("error");
            setErrorMsg(serverMsg || "Please check your payment details and try again.");
            return;
          }

          // Ambiguous (409, 502, 5xx, network drop): fail closed so the card
          // is never charged twice.
          setStatus("pending");
          setErrorMsg(
            serverMsg ||
              "We could not confirm the payment response. Please do NOT resubmit — refresh your balance shortly.",
          );
        });
    });
  }

  const formLocked =
    status === "loading" ||
    status === "submitting" ||
    status === "charging" ||
    status === "charged" ||
    status === "pending";

  const buttonLabel =
    status === "loading"
      ? "Loading secure payment…"
      : status === "submitting"
        ? "Encrypting card…"
        : status === "charging"
          ? "Processing payment…"
          : status === "pending"
            ? "Confirming payment…"
            : amountValid
              ? `Pay ${fmtMoney(parsedAmount)} with Card`
              : "Pay with Card";

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Add Credit with Credit Card</h2>
          <p className="text-xs text-gray-500">
            Processed securely via Authorize.Net — card numbers are encrypted in your browser and
            never stored on our servers.
          </p>
        </div>
        {config.sandbox && (
          <span className="px-2.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
            Sandbox Mode
          </span>
        )}
      </div>

      {status === "charged" && receipt ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-2">
          <p className="text-sm font-semibold text-green-900">{receipt.message}</p>
          <div className="text-xs text-green-800 space-y-0.5 font-mono">
            {receipt.trans_id && <div>Transaction ID: {receipt.trans_id}</div>}
            {receipt.card_summary && <div>Card: {receipt.card_summary}</div>}
            {typeof receipt.amount_credited === "number" && (
              <div>Amount Credited: {fmtMoney(receipt.amount_credited)}</div>
            )}
            {typeof receipt.new_balance === "number" && (
              <div>New Account Balance: {fmtMoney(receipt.new_balance)}</div>
            )}
          </div>
          <div className="pt-2">
            <button
              type="button"
              onClick={resetForAnotherPayment}
              className="px-3 py-1.5 rounded bg-white border border-green-300 text-xs font-medium text-green-900 hover:bg-green-100"
            >
              Make another payment
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Select or enter top-up amount (USD)
            </label>
            {presets.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {presets.map((preset) => {
                  const selected = Number(amountInput) === preset;
                  return (
                    <button
                      key={preset}
                      type="button"
                      disabled={formLocked}
                      onClick={() => setAmountInput(String(preset))}
                      className={`px-3 py-1.5 rounded text-sm font-medium border transition ${
                        selected
                          ? "bg-brand text-white border-brand"
                          : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                      } disabled:opacity-50`}
                    >
                      {fmtMoney(preset)}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="max-w-xs">
              <input
                aria-label="Amount (USD)"
                type="number"
                min={config.min_amount}
                max={config.max_amount}
                step="0.01"
                disabled={formLocked}
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono disabled:bg-gray-50"
                placeholder="100.00"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Min {fmtMoney(config.min_amount)} · Max {fmtMoney(config.max_amount)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="anet-name" className="block text-xs font-medium text-gray-600 mb-1">
                Name on card
              </label>
              <input
                id="anet-name"
                autoComplete="cc-name"
                disabled={formLocked}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Smith"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm disabled:bg-gray-50"
              />
            </div>

            <div>
              <label htmlFor="anet-number" className="block text-xs font-medium text-gray-600 mb-1">
                Card number
              </label>
              <input
                id="anet-number"
                inputMode="numeric"
                autoComplete="cc-number"
                disabled={formLocked}
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardDigits(e.target.value))}
                placeholder="1234 5678 9012 3456"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono disabled:bg-gray-50"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-xl">
            <div>
              <label htmlFor="anet-exp" className="block text-xs font-medium text-gray-600 mb-1">
                Expiration (MM/YY)
              </label>
              <input
                id="anet-exp"
                inputMode="numeric"
                autoComplete="cc-exp"
                disabled={formLocked}
                value={exp}
                onChange={(e) => setExp(formatExpiryInput(e.target.value))}
                placeholder="MM/YY"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono disabled:bg-gray-50"
              />
            </div>

            <div>
              <label htmlFor="anet-cvc" className="block text-xs font-medium text-gray-600 mb-1">
                Security code (CVC)
              </label>
              <input
                id="anet-cvc"
                inputMode="numeric"
                autoComplete="cc-csc"
                disabled={formLocked}
                value={cardCode}
                onChange={(e) => setCardCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="123"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono disabled:bg-gray-50"
              />
            </div>

            <div>
              <label htmlFor="anet-zip" className="block text-xs font-medium text-gray-600 mb-1">
                Billing ZIP
              </label>
              <input
                id="anet-zip"
                autoComplete="postal-code"
                disabled={formLocked}
                value={zip}
                onChange={(e) => setZip(e.target.value.slice(0, 12))}
                placeholder="10001"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono disabled:bg-gray-50"
              />
            </div>
          </div>

          {errorMsg && (
            <div
              role="alert"
              className={`rounded border px-3 py-2 text-xs ${
                status === "pending"
                  ? "border-amber-300 bg-amber-50 text-amber-900"
                  : "border-red-200 bg-red-50 text-red-700"
              }`}
            >
              {errorMsg}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={formLocked || !amountValid}
              className="bg-brand hover:bg-brand-dark text-white rounded px-5 py-2.5 text-sm font-medium disabled:opacity-50"
            >
              {buttonLabel}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export function BillingPage() {
  const qc = useQueryClient();
  const balances = useQuery({ queryKey: ["balances"], queryFn: listBalances });
  const payments = useQuery({ queryKey: ["payments"], queryFn: listPayments });
  const cardConfig = useQuery({
    queryKey: ["card-payment-config"],
    queryFn: getCardPaymentConfig,
  });

  const cardEnabled = Boolean(cardConfig.data?.enabled);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-1">Billing</h1>
        <p className="text-sm text-gray-500 mb-4">
          {cardEnabled
            ? "Add credit with a credit card and review your account balance and payment history."
            : "Account balances and payment history (read-only)."}
        </p>

        {cardEnabled && cardConfig.data && (
          <div className="mb-8">
            <CreditCardTopUpPanel
              config={cardConfig.data}
              onCredited={() => {
                qc.invalidateQueries({ queryKey: ["balances"] });
                qc.invalidateQueries({ queryKey: ["payments"] });
              }}
            />
          </div>
        )}

        {balances.isLoading ? (
          <p className="text-gray-500">Loading balances…</p>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-2">Account</th>
                  <th className="px-4 py-2 text-right">Credit balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(balances.data ?? []).map((b) => (
                  <tr key={b.usr_id}>
                    <td className="px-4 py-2 font-medium">{b.username}</td>
                    <td
                      className={`px-4 py-2 text-right font-mono ${b.credit <= 0 ? "text-red-600" : "text-gray-800"}`}
                    >
                      {fmtMoney(b.credit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(balances.data ?? []).length === 0 && (
              <p className="text-gray-400 text-sm p-4">No accounts.</p>
            )}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-3">Payment History</h2>
        {payments.isLoading ? (
          <p className="text-gray-500">Loading payments…</p>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Account</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(payments.data ?? []).map((p) => (
                  <tr key={p.payment_id}>
                    <td className="px-4 py-2 text-gray-500 font-mono">{p.paid_date || "—"}</td>
                    <td className="px-4 py-2">{p.username || `#${p.usr_id}`}</td>
                    <td className="px-4 py-2">{p.type || "—"}</td>
                    <td className="px-4 py-2 text-gray-600">{p.description || "—"}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmtMoney(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(payments.data ?? []).length === 0 && (
              <p className="text-gray-400 text-sm p-4">No payments recorded.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
